import React, { useEffect, useState } from "react";
import { Button, Empty, Message, Pagination, Table, Tooltip } from "@arco-design/web-react";
import { IconDelete, IconPlus } from "@arco-design/web-react/icon";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import Content from "../Layout/Content";
import LoadingState from "../Layout/LoadingState";
import SummaryCard from "../Layout/SummaryCard";
import ArchiveCampaignDialog from "./ArchiveCampaignDialog";
import CampaignDialog from "./CampaignDialog";

import { useAuth0 } from '@auth0/auth0-react';
import { FetchApi, PostApi } from '../Api/AuthenticatedApi';
import SimpleBreadcrumbs from "../Components/Breadcrumbs/SimpleBreadcrumbs";

// MUI -> Arco. The MUI Table/TableHead/TableBody/TableRow/TableCell +
// EnhancedTableHead JSX is rewritten into an Arco Table columns config + data
// source, with selection moved from per-row checkboxes to Arco's rowSelection
// (the header "select all" checkbox is rowSelection's). Sorting was a no-op in
// the original (handleRequestSort only logged), so the single "Name" column is
// kept without a functional sorter. TablePagination -> Arco Pagination.
// CircularProgress -> Spin; Toolbar -> a styled flex bar; Button contained ->
// type="primary"/secondary with startIcon -> icon; Tooltip title -> content.
// makeStyles -> styled-components; theme.spacing(n) inlined at 8px/unit.

const Toolbar = styled.div`
    display: flex;
    align-items: center;
    min-height: 56px;
    padding: 0 8px;
`;

const Grow = styled.div`
    flex-grow: 1;
`;

const DeleteButton = styled.span`
    margin-left: 8px;
`;

const ROWS_PER_PAGE = 10;

export default function Campaigns() {
    const [selected, setSelected] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(ROWS_PER_PAGE);

    let history = useNavigate();
    const { getAccessTokenSilently } = useAuth0();

    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isDataUpdated, setIsDataUpdated] = useState(false);


    const handleCampaignAdded = (campaignRecord) => {
        setLoading(true);

        PostApi(getAccessTokenSilently,
            "/api/campaigns/",
            (data) => {
                setLoading(false);
                setIsDataUpdated((prevIsDataUpdated) => !prevIsDataUpdated);
                Message.success("Campaign created");
            },
            (response) => {
                setLoading(false);
                history("/login");
            }, (response) => {
                setLoading(false);
                Message.error("Failed to create campaign");
            }, campaignRecord)
    }

    useEffect(() => {
        FetchApi(getAccessTokenSilently,
            "/api/campaigns/?" + new URLSearchParams({ page: page, count: rowsPerPage }),
            (data) => {
                setLoading(false);
                setRows((data && data.campaigns) || []);
                setTotalCount((data && data.total_count) || 0);
            },
            (response) => {
                setLoading(false);
                history("/login");
            }, (response) => {
                setLoading(false);
                Message.error("Failed to load campaigns");
            })
    }, [history, rowsPerPage, page, getAccessTokenSilently, isDataUpdated])

    if (loading) {
        return <LoadingState />;
    }

    const handleChangePage = (newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (size) => {
        setRowsPerPage(size);
        setPage(0);
    };

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
        },
    ];

    const rowSelection = {
        selectedRowKeys: selected,
        onChange: (selectedRowKeys) => {
            setSelected(selectedRowKeys);
        },
    };

    const tableRows = rows && 'map' in rows ? rows : [];

    return (
        <Content>
            {/* <div
                style={{
                    height: "200px",
                    backgroundPosition: "center",
                    backgroundSize: "cover",
                    filter: "contrast(75%)",
                    backgroundImage: "url(/img/wallpaper.jpeg)",
                }}
            /> */}

            <SimpleBreadcrumbs
                elements={
                    [
                        { name: "Signups" },
                    ]
                }

            />

            <Toolbar>
                <Grow />
                <CampaignDialog
                    onSave={(campaignRecord) => {
                        handleCampaignAdded(campaignRecord);
                    }}
                    render={(open) => (
                        <div>
                            <Button
                                type="primary"
                                icon={<IconPlus />}
                                onClick={open}
                            >
                                Add Campaign
                            </Button>
                        </div>
                    )}
                />
                {selected.length > 0 && (
                    <Tooltip content={"Archive"}>
                        {/*TODO(Eric Harrison): make this for archive */}
                        <ArchiveCampaignDialog
                            ids={selected}
                            onSave={() => {
                                Message.success(
                                    `${selected.length} Campaign${selected.length > 1 ? "s" : ""} Archived`
                                );
                                setSelected([]);
                            }}
                            render={(open) => (
                                <DeleteButton>
                                    <Button
                                        type="secondary"
                                        icon={<IconDelete />}
                                        onClick={open}
                                    >
                                        {" "}
                                        Archive {selected.length} selected
                                    </Button>
                                </DeleteButton>
                            )}
                        />
                    </Tooltip>

                )}
            </Toolbar>




            <SummaryCard
                title={"Signups"}
                subTitle={"Gather emails from your customers"}

            >
                <>
                    <Table
                        size="small"
                        rowKey="id"
                        columns={columns}
                        data={tableRows}
                        rowSelection={rowSelection}
                        pagination={false}
                        noDataElement={
                            <Empty description="No campaigns yet">
                                <CampaignDialog
                                    onSave={(campaignRecord) => {
                                        handleCampaignAdded(campaignRecord);
                                    }}
                                    render={(open) => (
                                        <Button type="primary" style={{ marginTop: 12 }} onClick={open}>
                                            Create your first campaign
                                        </Button>
                                    )}
                                />
                            </Empty>
                        }
                        onRow={(row) => ({
                            style: { cursor: "pointer" },
                            onClick: (e) => {
                                if (
                                    e.target.type === "checkbox" ||
                                    (typeof e.target.className === "string" &&
                                        e.target.className.indexOf("checkbox") > -1)
                                ) {
                                    return;
                                }
                                history(`/signups/${row.id}/edit`);
                            },
                        })}
                    />
                    <Pagination
                        style={{ marginTop: 8 }}
                        sizeCanChange
                        pageSizeChangeResetCurrent
                        total={totalCount}
                        pageSize={rowsPerPage}
                        current={page + 1}
                        sizeOptions={[5, 10, 25]}
                        onChange={(current) => handleChangePage(current - 1)}
                        onPageSizeChange={handleChangeRowsPerPage}
                    />
                </>
            </SummaryCard>

        </Content>
    );
}
